import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Loader as Loader2, Crown, Medal, Award, Activity, Heart, Play, Mic, User, Music } from 'lucide-react';
import { KioskService, KioskLeaderboardResponse, KioskPlayableSubmission, getStoredKioskSession } from '../services/KioskService';
import { useKioskBasePath } from '../hooks/useKioskBasePath';
import ShaderBackground from '../components/ShaderBackground';

export const SingAlongLeaderboard: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const basePath = useKioskBasePath();
  const [data, setData] = useState<KioskLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [myLikes, setMyLikes] = useState<Set<string>>(new Set());
  const [likingId, setLikingId] = useState<string | null>(null);
  const [playableMap, setPlayableMap] = useState<Record<string, KioskPlayableSubmission>>({});

  const loadLikes = useCallback(async () => {
    if (!slug) return;
    try {
      const likes = await KioskService.getLikes(slug);
      setLikeCounts(likes.counts);
      setMyLikes(new Set(likes.my_likes));
    } catch {
      // non-critical
    }
  }, [slug]);

  const loadPlayable = useCallback(async () => {
    if (!slug) return;
    try {
      const subs = await KioskService.getPlayableSubmissions(slug);
      const map: Record<string, KioskPlayableSubmission> = {};
      for (const s of subs) map[s.participant_id] = s;
      setPlayableMap(map);
    } catch {
      // non-critical
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    const load = async () => {
      try {
        const d = await KioskService.getLeaderboard(slug);
        if (active) setData(d);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    loadLikes();
    loadPlayable();
    const interval = setInterval(() => { load(); loadLikes(); }, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [slug, loadLikes, loadPlayable]);

  const handleLike = async (participantId: string) => {
    if (likingId) return;
    setLikingId(participantId);
    try {
      const result = await KioskService.toggleLike(participantId);
      setLikeCounts(prev => ({ ...prev, [participantId]: result.like_count }));
      setMyLikes(prev => {
        const next = new Set(prev);
        if (result.liked) next.add(participantId);
        else next.delete(participantId);
        return next;
      });
    } catch {
      // ignore
    } finally {
      setLikingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80 p-8 text-center">
        <div>
          <Trophy className="w-12 h-12 text-white/40 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Leaderboard unavailable</h1>
          <p className="text-white/60 text-sm">{error || 'League not found'}</p>
        </div>
      </div>
    );
  }

  const { league, entries } = data;
  const accent = league.accent_color || '#f97316';
  const primary = league.primary_color || '#f97316';
  const secondary = league.secondary_color || '#3b82f6';

  const session = getStoredKioskSession();
  const mySlug = session?.leagueSlug === slug ? session : null;

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown size={32} className="text-yellow-400 drop-shadow" />;
    if (rank === 2) return <Medal size={32} className="text-gray-200 drop-shadow" />;
    if (rank === 3) return <Award size={32} className="text-amber-500 drop-shadow" />;
    return null;
  };

  return (
    <div className="relative min-h-screen bg-slate-950 text-white">
      <div className="fixed inset-0 z-0 overflow-hidden">
        <ShaderBackground preset="aurora" primaryColor={primary} secondaryColor={secondary} />
      </div>
      <div className="fixed inset-0 z-0 bg-black/55" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="bg-black/60 backdrop-blur-sm border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 py-2 flex items-center justify-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">
              Powered by The Singing League
            </span>
            <img src="/TSL_Logo.png" alt="The Singing League" className="h-12 md:h-14 object-contain" />
          </div>
        </div>

        <header
          className="relative border-b border-white/10 shadow-2xl backdrop-blur-md"
          style={{ background: `linear-gradient(135deg, ${primary}E6, ${secondary}CC)` }}
        >
          <div className="absolute inset-0 bg-black/25 pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-8 py-6 flex items-center gap-4">
            {league.logo_url ? (
              <img
                src={league.logo_url}
                alt={league.name}
                className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl object-contain bg-white/95 p-1.5 shadow-lg border border-white/20"
              />
            ) : (
              <div
                className="h-14 w-14 sm:h-16 sm:w-16 rounded-xl flex items-center justify-center font-black text-2xl shadow-lg border border-white/20"
                style={{ backgroundColor: accent }}
              >
                {(league.name || 'S').charAt(0)}
              </div>
            )}
            <div>
              <div className="text-2xl sm:text-3xl font-black tracking-tight leading-tight drop-shadow-lg">
                {league.name}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs uppercase tracking-[0.25em] mb-4">
              <Trophy className="w-3.5 h-3.5" style={{ color: accent }} /> Live Leaderboard
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight drop-shadow-lg">
              Top Singers
            </h1>
          </div>

          {entries.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-16 text-center">
              <Trophy className="w-20 h-20 text-white/30 mx-auto mb-5" />
              <h2 className="text-2xl font-black mb-2">No scores yet</h2>
              <p className="text-white/60">Waiting for participants to sing their hearts out.</p>
            </div>
          ) : (
            <>
              {podium.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                  {podium.map((e, idx) => {
                    const rank = idx + 1;
                    const sub = playableMap[e.participant_id];
                    const hasPlayback = !!sub && (!!sub.mixed_audio_url || sub.mix_status === 'failed');
                    const isVocalsOnly = hasPlayback && !sub.mixed_audio_url;
                    return (
                      <div
                        key={e.participant_id}
                        className="relative rounded-2xl p-7 border-2 border-white/10 bg-white/5 backdrop-blur-md transition-all hover:scale-[1.02] text-center shadow-2xl"
                        style={{
                          borderColor: rank === 1 ? accent : undefined,
                          boxShadow: rank === 1 ? `0 20px 60px ${accent}40` : undefined,
                        }}
                      >
                        <div className="absolute top-4 right-4">{getRankIcon(rank)}</div>
                        {hasPlayback && (
                          <a
                            href={`/${basePath}/${slug}/share/${sub.submission_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="absolute top-4 left-4 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition hover:scale-110"
                            style={{ backgroundColor: accent }}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            {isVocalsOnly ? (
                              <Mic size={14} className="text-slate-900" />
                            ) : (
                              <Play size={16} className="text-slate-900 ml-0.5" fill="currentColor" />
                            )}
                          </a>
                        )}
                        <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2 font-semibold">
                          #{rank}
                        </div>
                        <h3 className="font-black text-2xl mb-2 truncate">{e.display_name}</h3>
                        <div className="text-5xl font-black mt-4" style={{ color: accent }}>
                          {Number(e.score).toFixed(1)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1">Points</div>
                        {mySlug && mySlug.participantId !== e.participant_id && (
                          <button
                            onClick={(ev) => { ev.stopPropagation(); handleLike(e.participant_id); }}
                            disabled={likingId === e.participant_id}
                            className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition hover:scale-105 active:scale-95"
                            style={{
                              backgroundColor: myLikes.has(e.participant_id) ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)',
                              color: myLikes.has(e.participant_id) ? '#ef4444' : 'rgba(255,255,255,0.7)',
                              border: `1px solid ${myLikes.has(e.participant_id) ? '#ef4444' : 'rgba(255,255,255,0.15)'}`,
                            }}
                          >
                            <Heart size={14} fill={myLikes.has(e.participant_id) ? '#ef4444' : 'none'} stroke={myLikes.has(e.participant_id) ? '#ef4444' : 'currentColor'} />
                            {likeCounts[e.participant_id] || 0}
                          </button>
                        )}
                        {(!mySlug || mySlug.participantId === e.participant_id) && (likeCounts[e.participant_id] || 0) > 0 && (
                          <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-white/50">
                            <Heart size={12} className="text-red-400" fill="#f87171" />
                            {likeCounts[e.participant_id]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {rest.length > 0 && (
                <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl overflow-hidden shadow-2xl">
                  <ol className="divide-y divide-white/10">
                    {rest.map((e) => {
                      const sub = playableMap[e.participant_id];
                      const hasPlayback = !!sub && (!!sub.mixed_audio_url || sub.mix_status === 'failed');
                      const isVocalsOnly = hasPlayback && !sub.mixed_audio_url;
                      return (
                        <li
                          key={e.participant_id}
                          className="flex items-center px-5 py-4 hover:bg-white/5 transition"
                        >
                          <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-base mr-4 bg-white/10 border border-white/15">
                            {e.rank}
                          </div>
                          {hasPlayback && (
                            <a
                              href={`/${basePath}/${slug}/share/${sub.submission_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="w-9 h-9 rounded-full flex items-center justify-center mr-3 shrink-0 transition hover:scale-110 active:scale-95 shadow-lg"
                              style={{ backgroundColor: accent }}
                            >
                              {isVocalsOnly ? (
                                <Mic size={12} className="text-slate-900" />
                              ) : (
                                <Play size={14} className="text-slate-900 ml-0.5" fill="currentColor" />
                              )}
                            </a>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-lg truncate">{e.display_name}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            {mySlug && mySlug.participantId !== e.participant_id && (
                              <button
                                onClick={(ev) => { ev.stopPropagation(); handleLike(e.participant_id); }}
                                disabled={likingId === e.participant_id}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition hover:scale-105 active:scale-95"
                                style={{
                                  backgroundColor: myLikes.has(e.participant_id) ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.08)',
                                  color: myLikes.has(e.participant_id) ? '#ef4444' : 'rgba(255,255,255,0.6)',
                                  border: `1px solid ${myLikes.has(e.participant_id) ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
                                }}
                              >
                                <Heart size={12} fill={myLikes.has(e.participant_id) ? '#ef4444' : 'none'} stroke={myLikes.has(e.participant_id) ? '#ef4444' : 'currentColor'} />
                                {likeCounts[e.participant_id] || 0}
                              </button>
                            )}
                            {(!mySlug || mySlug.participantId === e.participant_id) && (likeCounts[e.participant_id] || 0) > 0 && (
                              <span className="flex items-center gap-1 text-xs text-white/40">
                                <Heart size={11} className="text-red-400" fill="#f87171" /> {likeCounts[e.participant_id]}
                              </span>
                            )}
                            <div className="flex items-center gap-2">
                              <Activity size={16} style={{ color: accent }} />
                              <span className="text-2xl font-black tabular-nums" style={{ color: accent }}>
                                {Number(e.score).toFixed(1)}
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </>
          )}
        </main>

        <footer className="px-4 sm:px-8 py-6 border-t border-white/10 bg-black/40 backdrop-blur-md pb-24">
          <div className="max-w-5xl mx-auto text-center text-xs text-white/40">
            Powered by The Singing League
          </div>
        </footer>

        {mySlug ? (
          <nav className="fixed bottom-0 inset-x-0 z-50 bg-black/80 backdrop-blur-xl border-t border-white/10 safe-area-pb">
            <div className="max-w-md mx-auto flex items-stretch justify-around">
              <Link
                to={`/${basePath}/${slug}/leaderboard`}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-white transition"
              >
                <Trophy size={20} style={{ color: accent }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                  Leaderboard
                </span>
              </Link>
              <Link
                to={`/${basePath}/${slug}/me`}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-white/60 hover:text-white transition"
              >
                <User size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">My Page</span>
              </Link>
              <Link
                to={`/${basePath}/${slug}`}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-white/60 hover:text-white transition"
              >
                <Music size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Sing</span>
              </Link>
            </div>
          </nav>
        ) : (
          <nav className="fixed bottom-0 inset-x-0 z-50 bg-black/80 backdrop-blur-xl border-t border-white/10 safe-area-pb">
            <div className="max-w-md mx-auto flex items-stretch justify-around">
              <Link
                to={`/${basePath}/${slug}/leaderboard`}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-white transition"
              >
                <Trophy size={20} style={{ color: accent }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                  Leaderboard
                </span>
              </Link>
              <Link
                to={`/${basePath}/${slug}`}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-3 text-white/60 hover:text-white transition"
              >
                <Mic size={20} />
                <span className="text-[10px] font-semibold uppercase tracking-wider">Join</span>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};

export default SingAlongLeaderboard;
